import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
	DialogClose,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { WeekPicker } from "@/components/ui/week-picker";
import {
	updateSprint,
	type Sprint,
	type SprintStatus,
} from "@/services/sprint.service";
import { toast } from "sonner";

type WeekRange = { start: Date; end: Date };

const EMPTY_FORM = {
	name: "",
	week: null as WeekRange | null,
	status: "planned" as SprintStatus,
};

export function EditSprintDialog({
	open,
	onClose,
	sprint,
	onSaved,
}: {
	open: boolean;
	onClose: () => void;
	sprint: Sprint;
	onSaved: (updated: Sprint) => void;
}) {
	const [form, setForm] = useState(EMPTY_FORM);
	const [submitting, setSubmitting] = useState(false);
	const [errors, setErrors] = useState<{
		name?: string;
		week?: string;
		submit?: string;
	}>({});

	useEffect(() => {
		if (!open) return;
		const start = new Date(sprint.start_date + "T00:00:00");
		const end = new Date(sprint.end_date + "T00:00:00");
		setForm({ name: sprint.name, week: { start, end }, status: sprint.status });
		setErrors({});
	}, [open, sprint]);

	async function handleSubmit() {
		const errs: typeof errors = {};
		if (!form.name.trim()) errs.name = "Sprint name is required.";
		if (!form.week) errs.week = "Please select a week.";
		if (Object.keys(errs).length > 0) {
			setErrors(errs);
			return;
		}
		setSubmitting(true);
		setErrors({});
		try {
			const updated = await updateSprint(sprint.id, {
				name: form.name.trim(),
				start_date: format(form.week!.start, "yyyy-MM-dd"),
				end_date: format(form.week!.end, "yyyy-MM-dd"),
				status: form.status,
			});
			onSaved(updated);
			onClose();
			toast.success("Sprint updated", { description: updated.name });
		} catch (err: any) {
			const msg = err?.response?.data?.message ?? "Failed to update sprint.";
			setErrors({ submit: msg });
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
			<DialogContent className="max-w-[440px]">
				<DialogHeader>
					<DialogTitle>Edit Sprint</DialogTitle>
					<DialogDescription>
						Sprints cover one work week (Mon – Fri).
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div>
						<label className="text-sm font-medium text-muted-foreground mb-1.5 block">
							Sprint Name <span className="text-danger">*</span>
						</label>
						<Input
							value={form.name}
							onChange={(e) => {
								setForm((p) => ({ ...p, name: e.target.value }));
								setErrors((p) => ({ ...p, name: undefined }));
							}}
							className={errors.name ? "border-danger" : ""}
						/>
						{errors.name && (
							<p className="text-xs text-danger mt-1">{errors.name}</p>
						)}
					</div>

					<div>
						<label className="text-sm font-medium text-muted-foreground mb-1.5 block">
							Week <span className="text-danger">*</span>
						</label>
						<WeekPicker
							value={form.week}
							onChange={(w) => {
								setForm((p) => ({ ...p, week: w }));
								setErrors((p) => ({ ...p, week: undefined }));
							}}
						/>
						{errors.week && (
							<p className="text-xs text-danger mt-1">{errors.week}</p>
						)}
					</div>

					<div>
						<label className="text-sm font-medium text-muted-foreground mb-1.5 block">
							Status
						</label>
						<SearchableSelect
							value={form.status}
							onValueChange={(v) =>
								setForm((p) => ({ ...p, status: v as SprintStatus }))
							}
							options={[
								{ value: "planned", label: "Planned" },
								{ value: "active", label: "Active" },
								{ value: "completed", label: "Completed" },
							]}
						/>
					</div>

					{errors.submit && (
						<p className="text-xs text-danger">{errors.submit}</p>
					)}
				</div>

				<DialogFooter>
					<DialogClose asChild>
						<Button variant="outline" disabled={submitting}>
							Cancel
						</Button>
					</DialogClose>
					<Button onClick={handleSubmit} disabled={submitting}>
						{submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
						Save Changes
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
